import { createContext, useCallback, useContext, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import {
  useCaptureMarkersQuery, useCaptureSettingsQuery, useCreateCaptureMarkerMutation,
  useMapCaptureMarkerMutation,
  useRemoveCaptureMarkerMutation, useSetCaptureMarkerEnabledMutation,
  useStartGmailMutation, useStartMicrosoftMutation, useUpdateCaptureMarkerMutation,
  useCrmTagsQuery,
  type CaptureCommercialStage, type CaptureMarkerCapability, type CaptureMarkerMapping,
  type CaptureMarkerMappingInput, type CaptureMarkerProvider, type CrmTagVocabulary,
} from './api.js';
import { CrmTagChips, CrmTagPicker, EMPTY_VOCABULARY } from './CrmTagPicker.js';
import { fromSelection, sameSelection, toSelection, withdrawnNote } from './crmTagSelection.js';
import { Alert, Button, Spinner } from './ui.js';
import { rememberUpgrade } from './oauthOutcome.js';
import { DANGER } from './ReviewDrawer.js';

/**
 * Capture labels: the Gmail labels or Outlook categories that select mail for capture,
 * each with the CRM tags its contacts start with.
 *
 * Two promises are made on this screen and both have to stay true. Only mail carrying one
 * of these labels is read — so the list IS the boundary of what ContactPilot sees. And
 * nothing is created in the mailbox unless the customer pressed a button that says so.
 *
 * Exactly two ways to add one: use a label that already exists, or create one. Both
 * choose tags in place, with the one picker over GET /crm-tags; nobody is sent elsewhere
 * to choose a tag.
 */

const WRAP = '[overflow-wrap:anywhere]';
const FOCUS = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900';
const EMPTY: CaptureMarkerMappingInput = { commercialStage: null, relationshipTypeKeys: [], crmTagKeys: [] };
/** Names a new label might take. They only fill the name; tags are chosen separately. */
const NAME_SUGGESTIONS = ['Prospect', 'Lead', 'Existing Customer', 'Send to CRM'] as const;

interface Words {
  app: string; noun: string; nouns: string; Noun: string;
}
const wordsFor = (p: CaptureMarkerProvider): Words => (p === 'gmail'
  ? { app: 'Gmail', noun: 'label', nouns: 'labels', Noun: 'Label' }
  : { app: 'Outlook', noun: 'category', nouns: 'categories', Noun: 'Category' });

/** A mapping's three fields as the tags a customer chose, and back. */
const idsOf = (m: CaptureMarkerMappingInput, vocab: CrmTagVocabulary) => toSelection(m, vocab);
const mappingFrom = (ids: readonly string[], vocab: CrmTagVocabulary): CaptureMarkerMappingInput => {
  const f = fromSelection(ids, vocab);
  return { ...f, commercialStage: f.commercialStage as CaptureCommercialStage | null };
};

/**
 * What a label means for its contacts: one CRM tag picker, zero or many tags.
 *
 * Use-existing, create and edit all use it. The tags come from GET /crm-tags — the same
 * list Review and image and file capture offer — never a free-text field.
 */
function MappingEditor({ value, onChange, vocab, disabled, onManageTags, w }: {
  value: CaptureMarkerMappingInput;
  onChange: (v: CaptureMarkerMappingInput) => void;
  vocab: CrmTagVocabulary;
  disabled?: boolean;
  onManageTags?: () => void;
  w: Words;
}) {
  return (
    <div className="min-w-0 space-y-1">
      <CrmTagPicker
        vocab={vocab}
        selected={idsOf(value, vocab)}
        onChange={(ids) => onChange(mappingFrom(ids, vocab))}
        disabled={disabled}
        legend="CRM tags"
        empty={`None — you choose them in Review for each email marked with this ${w.noun}.`}
        onManageTags={onManageTags}
      />
      {onManageTags ? (
        <button
          type="button" onClick={onManageTags}
          className={`inline-flex min-h-11 items-center text-sm text-slate-600 underline hover:text-slate-900 ${FOCUS}`}
        >
          Manage CRM tags
        </button>
      ) : null}
    </div>
  );
}

/** Server codes, as sentences. Conflicts carry a person-readable message of their own. */
function errorText(e: unknown, w: Words): string {
  const data = (e as { data?: { error?: string; code?: string; message?: string } })?.data;
  const code = (data?.code ?? data?.error ?? '').toUpperCase();
  const status = (e as { status?: number })?.status;
  if (status === 403) return 'Only a workspace owner or admin can change this.';
  return outcomeText(code, w) ?? data?.message ?? 'That could not be saved. Try again.';
}

function outcomeText(code: string, w: Words): string | null {
  switch (code.toUpperCase()) {
    case 'MARKER_ALREADY_MAPPED': return `That ${w.noun} is already in use.`;
    case 'UNKNOWN_CRM_OPTION': return 'One of those CRM tags is no longer available. Reload the page and choose again.';
    case 'MARKER_CREATE_NOT_PERMITTED':
    case 'MARKER_CREATE_DISABLED': return `ContactPilot needs permission to create ${w.nouns} in ${w.app}. Choose “Allow ${w.noun} creation” to add it.`;
    case 'MARKER_DELETE_NOT_PERMITTED': return `ContactPilot needs ${w.noun} permission to delete ${w.nouns} it created. Choose “Allow ${w.noun} creation” to add it.`;
    case 'MARKER_DELETE_UNSUPPORTED': return `Delete the ${w.noun} in ${w.app}; ContactPilot does not delete ${w.nouns} there.`;
    case 'MARKER_NOT_MANAGED': return `ContactPilot only deletes ${w.nouns} it created; this one is yours.`;
    case 'PROVIDER_MARKER_MISSING': return `That ${w.noun} no longer exists in ${w.app}.`;
    case 'CAPTURE_MARKER_NOT_FOUND': return `That ${w.noun} does not exist in this mailbox.`;
    case 'CAPTURE_MARKER_INVALID': return `Choose or type a ${w.noun} from this mailbox.`;
    default: return null;
  }
}

/** The dialog's own close, so Cancel asks the same question Escape and the backdrop do. */
const DialogClose = createContext<(() => void) | null>(null);

/**
 * A small modal-looking panel. Not `aria-modal`: there is no focus trap, and claiming one
 * would be a promise not kept. Escape and the backdrop close it — after asking, when the
 * person has changed something (`dirty`), so a stray tap never loses a label's tags.
 */
function Dialog({ title, onClose, children, wide = false, dirty = false }: {
  title: string; onClose: () => void; children: ReactNode; wide?: boolean; dirty?: boolean;
}) {
  const id = useId();
  const askId = useId();
  const ref = useRef<HTMLDivElement | null>(null);
  const askRef = useRef<HTMLDivElement | null>(null);
  const [asking, setAsking] = useState(false);
  const requestClose = useCallback(() => { if (dirty) setAsking(true); else onClose(); }, [dirty, onClose]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Escape while asking means "keep editing", never a second, silent discard.
      if (asking) setAsking(false); else requestClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [asking, requestClose]);
  useEffect(() => { ref.current?.focus(); }, []);
  // The safe answer has focus: Enter keeps editing rather than discarding.
  useEffect(() => { if (asking) askRef.current?.querySelectorAll('button')[1]?.focus(); }, [asking]);
  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-black/30 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) requestClose(); }}
    >
      <div
        role="dialog" aria-labelledby={id} ref={ref} tabIndex={-1}
        className={`mx-auto my-[6vh] w-full min-w-0 ${wide ? 'max-w-lg' : 'max-w-md'} rounded-lg bg-white p-5 shadow-xl`}
      >
        <h3 id={id} className={`text-base font-semibold text-slate-900 ${WRAP}`}>{title}</h3>
        <DialogClose.Provider value={requestClose}>
          <div className="mt-3">{children}</div>
        </DialogClose.Provider>
        {asking ? (
          <div ref={askRef} role="alertdialog" aria-labelledby={askId} className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3" data-discard-question="">
            <p id={askId} className="text-sm font-medium text-amber-900">Discard your changes?</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button variant="secondary" className={`min-h-11 ${DANGER}`} onClick={onClose}>Discard</Button>
              <Button className="min-h-11" onClick={() => setAsking(false)}>Keep editing</Button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function Badge({ tone, children }: { tone: 'slate' | 'red'; children: ReactNode }) {
  const cls = {
    slate: 'bg-slate-100 text-slate-700 ring-slate-500/20',
    red: 'bg-red-50 text-red-800 ring-red-600/20',
  }[tone];
  return <span className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${cls}`}>{children}</span>;
}

/**
 * Why a label cannot be created, and the one button that changes that.
 *
 * Label creation is an added capability of the SAME connection — same account, same
 * stored mail — so the button says "Allow", never "Reconnect". It returns here, to Email
 * capture, where the person was.
 */
function CreatePermission({ provider, canEdit }: { provider: CaptureMarkerProvider; canEdit: boolean }) {
  const [startGmail, gmailState] = useStartGmailMutation();
  const [startMs, msState] = useStartMicrosoftMutation();
  const [error, setError] = useState<string | null>(null);
  const isGmail = provider === 'gmail';
  const busy = gmailState.isLoading || msState.isLoading;

  const allow = async () => {
    setError(null);
    try {
      const { authorizationUrl } = isGmail
        ? await startGmail({ returnPath: '/settings/email-capture', labelManagement: true }).unwrap()
        : await startMs({ returnPath: '/settings/email-capture', categoryManagement: true }).unwrap();
      rememberUpgrade(provider);
      window.location.assign(authorizationUrl);
    } catch {
      setError(`${isGmail ? 'Google' : 'Microsoft'} could not be opened. Try again.`);
    }
  };

  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-create-permission="">
      <p className="font-medium">
        {isGmail
          ? 'ContactPilot needs permission to create labels in Gmail.'
          : 'ContactPilot needs permission to create categories in Outlook.'}
      </p>
      <p className="mt-1">
        {isGmail
          ? 'With it, ContactPilot can create the labels you configure here so they appear in Gmail. It will not apply, remove or reorganize labels on your email. Google describes this permission as “See and edit your email labels.”'
          : 'With it, ContactPilot can list and create the categories you configure here so they appear in Outlook. It will not apply, remove or reorganize categories on your email. Microsoft describes this permission as reading and writing mailbox settings, which includes categories.'}
      </p>
      <Button className="mt-3 min-h-11" disabled={!canEdit || busy} onClick={() => void allow()}>
        {busy ? 'Opening…' : isGmail ? 'Allow label creation' : 'Allow category creation'}
      </Button>
      {error ? <p className="mt-2 text-red-800" role="status">{error}</p> : null}
    </div>
  );
}

/** The Save / Cancel pair every dialog ends with. */
function DialogActions({ children, onCancel }: { children: ReactNode; onCancel: () => void }) {
  const guarded = useContext(DialogClose);
  return (
    <div className="mt-5 flex flex-wrap gap-2">
      {children}
      <Button variant="secondary" className="min-h-11" onClick={guarded ?? onCancel}>Cancel</Button>
    </div>
  );
}

/**
 * Edit a label: its CRM tags, and whether it captures at all.
 *
 * Pausing lives here rather than on the row: it is a rare decision, and a checkbox on
 * every row read as one more thing to set.
 */
function EditDialog({ m, w, vocab, onClose, onResult, onManageTags }: {
  m: CaptureMarkerMapping; w: Words; vocab: CrmTagVocabulary;
  onClose: () => void; onManageTags?: () => void;
  onResult: (r: { tone: 'success' | 'error'; text: string }) => void;
}) {
  const [update, updateState] = useUpdateCaptureMarkerMutation();
  const [setEnabled, enabledState] = useSetCaptureMarkerEnabledMutation();
  const missing = m.state === 'provider_marker_missing';
  const [mapping, setMapping] = useState<CaptureMarkerMappingInput>({
    commercialStage: m.commercialStage,
    relationshipTypeKeys: [...m.relationshipTypeKeys],
    crmTagKeys: [...m.crmTagKeys],
  });
  const [active, setActive] = useState(m.state === 'active');
  const busy = updateState.isLoading || enabledState.isLoading;
  const tagsChanged = !sameSelection(idsOf(mapping, vocab), idsOf(m, vocab));
  const activeChanged = !missing && active !== (m.state === 'active');
  // A tag that is no longer available would be refused: it is removed first, never dropped.
  const blocked = withdrawnNote(idsOf(mapping, vocab), vocab);

  const save = async () => {
    try {
      if (tagsChanged) await update({ id: m.id, mapping }).unwrap();
      if (activeChanged) await setEnabled({ id: m.id, enabled: active }).unwrap();
      onResult({ tone: 'success', text: `Saved “${m.providerMarkerName}”. Emails captured earlier keep what they were given.` });
      onClose();
    } catch (e) { onResult({ tone: 'error', text: errorText(e, w) }); }
  };

  return (
    <Dialog title={`Edit “${m.providerMarkerName}”`} onClose={onClose} wide dirty={tagsChanged || activeChanged}>
      <div className="min-w-0 space-y-4" data-edit-label="">
        <MappingEditor value={mapping} onChange={setMapping} vocab={vocab} disabled={busy} onManageTags={onManageTags} w={w} />
        <label className="flex min-h-11 items-start gap-3 text-sm text-slate-800">
          <input
            type="checkbox" className="mt-0.5 h-5 w-5 shrink-0 rounded border-slate-300"
            checked={!missing && active} disabled={missing || busy}
            onChange={(e) => setActive(e.target.checked)}
          />
          <span>
            Capture emails with this {w.noun}
            <span className="block text-xs text-slate-500">
              {missing
                ? `This ${w.noun} no longer exists in ${w.app}, so nothing can be captured with it.`
                : `Turn off to pause it. Emails marked while it is paused are not captured.`}
            </span>
          </span>
        </label>
        <p className="text-xs text-slate-500">
          Applies to emails captured from now on. Emails captured earlier keep what this {w.noun} meant then.
        </p>
      </div>
      <DialogActions onCancel={onClose}>
        <Button className="min-h-11" disabled={busy || Boolean(blocked) || (!tagsChanged && !activeChanged)} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** One label in use: its name, the CRM tags it gives, Edit and Remove. */
function MappingRow({ m, w, vocab, canEdit, canDelete, onResult, onManageTags }: {
  m: CaptureMarkerMapping; w: Words; vocab: CrmTagVocabulary; canEdit: boolean;
  onManageTags?: () => void;
  /** Whether the grant allows deleting a label ContactPilot created. */
  canDelete: boolean;
  onResult: (r: { tone: 'success' | 'error'; text: string }) => void;
}) {
  const [remove, removeState] = useRemoveCaptureMarkerMutation();
  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [alsoDelete, setAlsoDelete] = useState(false);
  const missing = m.state === 'provider_marker_missing';
  const offerDelete = m.origin === 'created_by_inbox_signal' && m.provider === 'gmail';
  const ids = idsOf(m, vocab);

  const doRemove = async () => {
    try {
      const out = await remove({ id: m.id, deleteProviderMarker: offerDelete && alsoDelete }).unwrap();
      setRemoving(false);
      onResult({
        tone: 'success',
        text: out.providerDeleted
          ? `Removed “${m.providerMarkerName}” and deleted it from ${w.app}.`
          : `Removed “${m.providerMarkerName}”. The ${w.noun} stays in ${w.app}; emails carrying it are no longer captured.`,
      });
    } catch (e) { onResult({ tone: 'error', text: errorText(e, w) }); }
  };

  return (
    <li className="min-w-0 rounded-md border border-slate-200 p-3" data-capture-label="">
      <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0 flex-1">
          <p className="flex min-w-0 flex-wrap items-center gap-2">
            <span className={`min-w-0 text-sm font-medium text-slate-900 ${WRAP}`}>{m.providerMarkerName}</span>
            {missing
              ? <Badge tone="red">Missing in {w.app}</Badge>
              : m.state === 'disabled' ? <Badge tone="slate">Paused</Badge> : null}
          </p>
          <div className="mt-1.5 flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="shrink-0 text-sm text-slate-500">CRM tags:</span>
            {ids.length > 0
              ? <CrmTagChips ids={ids} vocab={vocab} />
              : <span className="text-sm text-slate-600">Choose in Review</span>}
          </div>
          {missing ? (
            <p className="mt-1 text-xs text-red-800">
              This {w.noun} was deleted or renamed in {w.app}; remove it here or recreate it.
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 gap-2">
          <Button variant="secondary" className="min-h-11 min-w-[4.5rem]" disabled={!canEdit} onClick={() => setEditing(true)}>
            Edit<span className="sr-only"> {m.providerMarkerName}</span>
          </Button>
          <Button variant="secondary" className="min-h-11 min-w-[4.5rem]" disabled={!canEdit} onClick={() => { setAlsoDelete(false); setRemoving(true); }}>
            Remove<span className="sr-only"> {m.providerMarkerName}</span>
          </Button>
        </div>
      </div>

      {editing ? (
        <EditDialog
          m={m} w={w} vocab={vocab} onClose={() => setEditing(false)}
          onResult={onResult} onManageTags={onManageTags}
        />
      ) : null}

      {removing ? (
        <Dialog title={`Remove “${m.providerMarkerName}”?`} onClose={() => setRemoving(false)}>
          <p className="text-sm text-slate-700">
            Emails carrying this {w.noun} will no longer be captured. Emails already captured stay in Review.
          </p>
          {offerDelete ? (
            <>
              <label className="mt-3 flex min-h-11 items-start gap-3 text-sm text-slate-800">
                <input
                  type="checkbox" className="mt-0.5 h-5 w-5 shrink-0 rounded border-slate-300"
                  checked={alsoDelete} disabled={!canDelete}
                  onChange={(e) => setAlsoDelete(e.target.checked)}
                />
                <span>
                  Also delete the label from Gmail
                  <span className="block text-xs text-slate-500">
                    {canDelete
                      ? 'ContactPilot created it, so it can remove it. Gmail removes it from every email that carries it.'
                      : 'Allow label creation for this mailbox first to delete it from here.'}
                  </span>
                </span>
              </label>
              {!alsoDelete ? <p className="mt-1 text-xs text-slate-500">Your label stays in Gmail.</p> : null}
            </>
          ) : (
            <p className="mt-3 text-sm text-slate-600">Your {w.noun} stays in {w.app}.</p>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            <Button className="min-h-11" disabled={removeState.isLoading} onClick={() => void doRemove()}>
              {removeState.isLoading ? 'Removing…' : 'Remove'}
            </Button>
            <Button variant="secondary" className="min-h-11" onClick={() => setRemoving(false)}>Keep it</Button>
          </div>
        </Dialog>
      ) : null}
    </li>
  );
}

type Panel = 'existing' | 'create' | null;

/** "+ Use existing label": choose one of the mailbox's own, or type an Outlook category. */
function UseExistingDialog({ data, w, vocab, onClose, onResult, onManageTags }: {
  data: { choices: Array<{ providerMarkerId: string; name: string; mappedMarkerId: string | null }> | null };
  w: Words; vocab: CrmTagVocabulary; onManageTags?: () => void;
  onClose: () => void;
  onResult: (r: { tone: 'success' | 'error'; text: string }) => void;
}) {
  const [map, state] = useMapCaptureMarkerMutation();
  const [filter, setFilter] = useState('');
  const [chosen, setChosen] = useState<string>('');
  const [typed, setTyped] = useState('');
  const [mapping, setMapping] = useState<CaptureMarkerMappingInput>(EMPTY);
  const listing = data.choices !== null;
  const unmapped = (data.choices ?? []).filter((c) => c.mappedMarkerId === null);
  const needle = filter.trim().toLowerCase();
  const shown = needle ? unmapped.filter((c) => c.name.toLowerCase().includes(needle)) : unmapped;
  const name = listing ? unmapped.find((c) => c.providerMarkerId === chosen)?.name ?? '' : typed.trim();
  const canSave = !state.isLoading && (listing ? chosen !== '' : typed.trim() !== '')
    && !withdrawnNote(idsOf(mapping, vocab), vocab);

  const save = async () => {
    try {
      await map(listing ? { providerMarkerId: chosen, mapping } : { categoryName: typed.trim(), mapping }).unwrap();
      onResult({ tone: 'success', text: `Now capturing with “${name}”. Emails you mark with it from now on are captured.` });
      onClose();
    } catch (e) { onResult({ tone: 'error', text: errorText(e, w) }); }
  };

  return (
    <Dialog
      title={`Use an existing ${w.noun}`} onClose={onClose} wide
      dirty={chosen !== '' || typed.trim() !== '' || idsOf(mapping, vocab).length > 0}
    >
      <div className="min-w-0 space-y-4" data-map-existing="">
        {listing ? (
          <div className="min-w-0">
            <label className="block text-sm text-slate-700" htmlFor="map-existing-filter">Find a {w.noun}</label>
            <input
              id="map-existing-filter" type="search" value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder={`Filter ${unmapped.length} ${w.nouns}`}
              className={`mt-1 min-h-11 w-full min-w-0 rounded-md border border-slate-300 bg-white px-3 text-base sm:text-sm ${FOCUS}`}
            />
            {unmapped.length === 0 ? (
              <p className="mt-2 text-sm text-slate-600">
                Every {w.noun} in this mailbox is already in use. Create one in {w.app}, or create a new one here.
              </p>
            ) : shown.length === 0 ? (
              <p className={`mt-2 text-sm text-slate-600 ${WRAP}`}>No {w.noun} matches “{filter}”.</p>
            ) : (
              <div
                role="radiogroup" aria-label={`${w.app} ${w.nouns}`}
                className="mt-2 max-h-60 min-w-0 overflow-y-auto rounded-md border border-slate-200 bg-white"
              >
                {shown.map((c) => (
                  <label
                    key={c.providerMarkerId}
                    className={`flex min-h-11 min-w-0 items-center gap-3 border-b border-slate-100 px-3 py-2 text-sm last:border-b-0 ${
                      chosen === c.providerMarkerId ? 'bg-slate-100' : ''
                    }`}
                  >
                    <input
                      type="radio" name="map-existing-choice" className="h-5 w-5 shrink-0"
                      checked={chosen === c.providerMarkerId}
                      onChange={() => setChosen(c.providerMarkerId)}
                    />
                    <span className={`min-w-0 text-slate-800 ${WRAP}`}>{c.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="min-w-0">
            <label className="block text-sm text-slate-700" htmlFor="map-existing-category">Outlook category name</label>
            <input
              id="map-existing-category" type="text" value={typed} maxLength={255}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="Send to CRM"
              className={`mt-1 min-h-11 w-full min-w-0 rounded-md border border-slate-300 bg-white px-3 text-base sm:text-sm ${FOCUS}`}
            />
            <p className="mt-1 text-xs text-slate-500">
              This connection cannot list your categories, so type the name exactly as it appears in Outlook.
            </p>
          </div>
        )}
        <MappingEditor value={mapping} onChange={setMapping} vocab={vocab} disabled={state.isLoading} onManageTags={onManageTags} w={w} />
      </div>
      <DialogActions onCancel={onClose}>
        <Button className="min-h-11" disabled={!canSave} onClick={() => void save()}>
          {state.isLoading ? 'Saving…' : `Use ${w.noun}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** "+ Create label": made in the mailbox only when the grant allows it. */
function CreateDialog({ provider, capability, w, vocab, canEdit, onClose, onResult, onManageTags }: {
  provider: CaptureMarkerProvider; capability: CaptureMarkerCapability | null; w: Words;
  vocab: CrmTagVocabulary; canEdit: boolean; onManageTags?: () => void;
  onClose: () => void;
  onResult: (r: { tone: 'success' | 'error'; text: string }) => void;
}) {
  const [create, state] = useCreateCaptureMarkerMutation();
  const [name, setName] = useState('');
  const [mapping, setMapping] = useState<CaptureMarkerMappingInput>(EMPTY);
  const allowed = capability?.canCreate === true;
  const trimmed = name.trim();
  const disabled = !allowed || state.isLoading;

  const save = async () => {
    try {
      const out = await create({ name: trimmed, mapping }).unwrap();
      onResult({
        tone: 'success',
        text: `${out.providerCreated
          ? `Created “${out.marker.providerMarkerName}” in ${w.app}.`
          : `“${out.marker.providerMarkerName}” already existed in ${w.app}, so it is now in use.`} Now apply this ${w.noun} in ${w.app} to capture an email.`,
      });
      onClose();
    } catch (e) { onResult({ tone: 'error', text: errorText(e, w) }); }
  };

  return (
    <Dialog title={`Create a ${w.noun}`} onClose={onClose} wide dirty={trimmed !== '' || idsOf(mapping, vocab).length > 0}>
      <div className="min-w-0 space-y-4" data-create-label="">
        {!allowed ? <CreatePermission provider={provider} canEdit={canEdit} /> : null}
        <div className="min-w-0">
          <label className="block text-sm font-medium text-slate-800" htmlFor="create-label-name">Name</label>
          <input
            id="create-label-name" type="text" value={name} maxLength={225}
            disabled={disabled}
            onChange={(e) => setName(e.target.value)}
            placeholder={provider === 'gmail' ? 'Inbox Signal/Lead' : 'Inbox Signal: Lead'}
            aria-describedby="create-label-help"
            className={`mt-1 min-h-11 w-full min-w-0 rounded-md border border-slate-300 bg-white px-3 text-base disabled:bg-slate-100 sm:text-sm ${FOCUS}`}
          />
          <p id="create-label-help" className="mt-1 text-xs text-slate-500">
            {provider === 'gmail'
              ? 'The label as it will appear in Gmail. Use “/” to nest it, for example Inbox Signal/Lead.'
              : 'The category as it will appear in Outlook.'}
          </p>
          <ul className="mt-2 flex min-w-0 flex-wrap gap-1.5" aria-label="Name suggestions">
            {NAME_SUGGESTIONS.map((n) => (
              <li key={n}>
                <button
                  type="button" disabled={disabled} onClick={() => setName(n)}
                  aria-pressed={trimmed === n}
                  className={`inline-flex min-h-9 items-center rounded-full border px-3 text-sm disabled:opacity-50 ${
                    trimmed === n ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                  } ${FOCUS}`}
                >
                  {n}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <MappingEditor value={mapping} onChange={setMapping} vocab={vocab} disabled={disabled} onManageTags={onManageTags} w={w} />
      </div>
      <DialogActions onCancel={onClose}>
        <Button
          className="min-h-11" onClick={() => void save()}
          disabled={!allowed || !canEdit || state.isLoading || trimmed === '' || Boolean(withdrawnNote(idsOf(mapping, vocab), vocab))}
        >
          {state.isLoading ? 'Creating…' : `Create ${w.noun}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function CaptureLabels({ canEdit, onOpenConnections, onManageTags }: {
  canEdit: boolean; onOpenConnections?: () => void;
  /** Settings → CRM Tags, the one catalog every picker draws from. */
  onManageTags?: () => void;
}) {
  const settings = useCaptureSettingsQuery();
  const markers = useCaptureMarkersQuery();
  const tags = useCrmTagsQuery();
  const [panel, setPanel] = useState<Panel>(null);
  const [result, setResult] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  if (settings.isLoading || markers.isLoading || tags.isLoading) return <Spinner label="Loading your mailbox…" />;
  if (markers.error || !markers.data) return <Alert tone="error">Your capture labels could not be loaded. Reload the page to try again.</Alert>;

  const data = markers.data;
  const mailbox = settings.data?.mailbox ?? null;
  const vocab = tags.data ?? EMPTY_VOCABULARY;
  const provider = data.provider;

  if (provider === null) {
    return (
      <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
        <p>
          No mailbox is connected. Connect Gmail or Outlook first, then come back here to
          choose which emails are sent to your CRM.
        </p>
        {onOpenConnections ? (
          <Button variant="secondary" className="mt-3 min-h-11" onClick={onOpenConnections}>
            Go to Connections
          </Button>
        ) : null}
      </div>
    );
  }

  const w = wordsFor(provider);
  const canDelete = data.capability?.canCreate === true;
  const open = (p: Panel) => { setResult(null); setPanel(p); };
  const close = () => setPanel(null);
  const activeCount = data.mappings.filter((m) => m.state === 'active').length;

  return (
    <div className="min-w-0 space-y-4">
      <p className={`text-sm text-slate-600 ${WRAP}`}>
        Only emails carrying one of these {w.nouns} are read and analysed.
        {mailbox ? <> Mailbox: <span className="font-medium text-slate-800">{mailbox.address}</span>.</> : null}
      </p>

      {data.mappings.length === 0 ? (
        <p className="rounded-md border border-dashed border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          No {w.noun} is in use yet, so nothing is being captured.
        </p>
      ) : (
        <ul className="min-w-0 space-y-2" aria-label={`Capture ${w.nouns}`}>
          {data.mappings.map((m) => (
            <MappingRow
              key={m.id} m={m} w={w} vocab={vocab} canEdit={canEdit} canDelete={canDelete}
              onResult={setResult} onManageTags={onManageTags}
            />
          ))}
        </ul>
      )}
      {data.mappings.length > 0 && activeCount === 0 ? (
        <p className="text-sm text-amber-800">Every {w.noun} is paused or missing, so nothing is being captured.</p>
      ) : null}

      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap" data-label-actions="">
        <Button variant="secondary" className="min-h-11" disabled={!canEdit} onClick={() => open('existing')}>
          + Use existing {w.noun}
        </Button>
        <Button variant="secondary" className="min-h-11" disabled={!canEdit} onClick={() => open('create')}>
          + Create {w.noun}
        </Button>
      </div>

      {panel === 'existing' ? (
        <UseExistingDialog
          data={data} w={w} vocab={vocab} onClose={close} onResult={setResult}
          onManageTags={onManageTags}
        />
      ) : null}
      {panel === 'create' ? (
        <CreateDialog
          provider={provider} capability={data.capability} w={w} vocab={vocab} canEdit={canEdit}
          onClose={close} onResult={setResult} onManageTags={onManageTags}
        />
      ) : null}

      <div aria-live="polite">
        {result ? <Alert tone={result.tone}>{result.text}</Alert> : null}
      </div>

      <p className="text-xs text-slate-500">
        Marks you apply from now on are captured; emails marked before you configured a label are not.
      </p>
    </div>
  );
}
